import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, MapPin, ArrowRight, CalendarPlus, Navigation } from 'lucide-react';
import { practiceSchedule } from '../mock';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ICS_DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

function parseHM(hm) {
  const [h, m] = hm.split(':').map(Number);
  return { h, m };
}

function nextOccurrence(now, days, time24) {
  const { h, m } = parseHM(time24);
  let winner = null;
  for (const dow of days) {
    const candidate = new Date(now);
    candidate.setHours(h, m, 0, 0);
    const dayDiff = (dow - now.getDay() + 7) % 7;
    if (dayDiff === 0 && candidate <= now) {
      candidate.setDate(candidate.getDate() + 7);
    } else {
      candidate.setDate(candidate.getDate() + dayDiff);
    }
    if (!winner || candidate < winner) winner = candidate;
  }
  return winner;
}

function formatCountdown(ms) {
  if (ms <= 0) return 'Starting now';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const minutes = totalMin % 60;
  if (days >= 1) return `${days}d ${hours}h ${minutes}m`;
  if (hours >= 1) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatTime(date) {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Format a Date as local ICS DTSTART/DTEND value (YYYYMMDDTHHMMSS, floating local time)
function icsLocal(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    date.getFullYear().toString() +
    pad(date.getMonth() + 1) +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    pad(date.getMinutes()) +
    pad(date.getSeconds())
  );
}

function icsDateOnly(dateStr) {
  return dateStr.replaceAll('-', '');
}

function buildIcs() {
  const { preseason, regularSeason, switchDate } = practiceSchedule;
  const switchOn = new Date(`${switchDate}T00:00:00`);
  const now = new Date();

  // Phase 1 — Preseason: DTSTART = next preseason occurrence from today, UNTIL = day before switch
  const preStart = nextOccurrence(now, preseason.days, preseason.time24);
  const preEndHM = parseHM(preseason.endTime24);
  const preStartEnd = new Date(preStart);
  preStartEnd.setHours(preEndHM.h, preEndHM.m, 0, 0);
  const preByday = preseason.days.map((d) => ICS_DAY_CODES[d]).join(',');

  // Phase 2 — Regular season: DTSTART = first regular-season occurrence on/after switchOn
  const regStart = nextOccurrence(switchOn, regularSeason.days, regularSeason.time24);
  const regEndHM = parseHM(regularSeason.endTime24);
  const regStartEnd = new Date(regStart);
  regStartEnd.setHours(regEndHM.h, regEndHM.m, 0, 0);
  const regByday = regularSeason.days.map((d) => ICS_DAY_CODES[d]).join(',');

  const stamp = icsLocal(now) + 'Z';
  const uid1 = `preseason-${stamp}@brooklinejppatriots`;
  const uid2 = `regular-${stamp}@brooklinejppatriots`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Brookline JP Patriots//Practice Schedule//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:BJP Patriots Practice',
    'X-WR-TIMEZONE:America/New_York',
    // Preseason recurring event
    'BEGIN:VEVENT',
    `UID:${uid1}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${icsLocal(preStart)}`,
    `DTEND:${icsLocal(preStartEnd)}`,
    `RRULE:FREQ=WEEKLY;BYDAY=${preByday};UNTIL=${icsDateOnly(switchDate)}T000000Z`,
    'SUMMARY:Patriots Practice (Preseason)',
    `LOCATION:${preseason.location}`,
    'DESCRIPTION:Brookline JP Patriots Pop Warner practice. Preseason Mon–Thu 5:30–7:30 PM.',
    'END:VEVENT',
    // Regular season recurring event (no UNTIL — runs indefinitely)
    'BEGIN:VEVENT',
    `UID:${uid2}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${icsLocal(regStart)}`,
    `DTEND:${icsLocal(regStartEnd)}`,
    `RRULE:FREQ=WEEKLY;BYDAY=${regByday}`,
    'SUMMARY:Patriots Practice (Regular Season)',
    `LOCATION:${regularSeason.location}`,
    'DESCRIPTION:Brookline JP Patriots Pop Warner practice. Regular season Tue–Thu 5:30–7:30 PM.',
    'END:VEVENT',
    'END:VCALENDAR'
  ];
  return lines.join('\r\n');
}

function downloadIcs() {
  const blob = new Blob([buildIcs()], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'bjp-patriots-practice.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function NextPractice() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const { activePhase, next } = useMemo(() => {
    const switchOn = new Date(`${practiceSchedule.switchDate}T00:00:00`);
    const phase = now < switchOn ? practiceSchedule.preseason : practiceSchedule.regularSeason;
    const when = nextOccurrence(now, phase.days, phase.time24);
    return { activePhase: phase, next: { ...phase, when } };
  }, [now]);

  const endHM = parseHM(next.endTime24);
  const endDate = new Date(next.when);
  endDate.setHours(endHM.h, endHM.m, 0, 0);

  const dayLabel = DAY_NAMES[next.when.getDay()];
  const countdown = formatCountdown(next.when - now);
  const phaseDaysLabel = activePhase.days.map((d) => DAY_NAMES[d].slice(0, 3)).join(' • ');
  const directionsUrl = 'https://www.google.com/maps/dir/?api=1&destination=McLaughlin%20Playground%2C%20239%20Parker%20Hill%20Ave%2C%20Roxbury%2C%20MA';

  return (
    <section className="bg-white border-t-4 border-[#c8102e]" data-testid="next-practice-banner">
      <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-11 h-11 bg-[#c8102e] text-white flex items-center justify-center">
            <CalendarClock className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Next Practice · <span data-testid="next-practice-phase">{activePhase.label}</span></div>
            <div className="font-display text-[#0a1f44] text-2xl leading-none" data-testid="next-practice-day">{dayLabel.toUpperCase()}</div>
          </div>
        </div>

        <div className="hidden md:block h-10 w-px bg-[#0a1f44]/15" />

        <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-[#0a1f44]">
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50">Days</div>
            <div className="font-heading text-sm" data-testid="next-practice-days">{phaseDaysLabel}</div>
          </div>
          <div>
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50">Time</div>
            <div className="font-heading text-sm" data-testid="next-practice-time">
              {formatTime(next.when)} – {formatTime(endDate)}
            </div>
          </div>
          <div className="flex-1 relative group">
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50 flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</div>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="next-practice-location"
              aria-describedby="practice-directions-tooltip"
              className="inline-flex items-center gap-1.5 font-heading text-sm underline decoration-[#c8102e]/40 underline-offset-4 hover:text-[#c8102e] transition-colors"
            >
              {next.location} <Navigation className="w-3.5 h-3.5 shrink-0" />
            </a>
            <div
              id="practice-directions-tooltip"
              role="tooltip"
              data-testid="practice-directions-tooltip"
              className="absolute left-0 top-full mt-3 z-30 w-72 max-w-[calc(100vw-3rem)] bg-[#0a1f44] text-white p-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-[opacity,transform] duration-200 shadow-xl"
            >
              <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e] mb-1">How to get there</div>
              <div className="text-sm leading-relaxed">McLaughlin Playground is across from 239 Parker Hill Ave, Roxbury, MA.</div>
              <div className="font-heading uppercase tracking-widest text-[10px] mt-2">Open turn-by-turn directions ↗</div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50">In</div>
            <div className="font-display text-2xl text-[#c8102e] leading-none" data-testid="next-practice-countdown">{countdown}</div>
          </div>
          <button
            onClick={downloadIcs}
            data-testid="next-practice-ics-btn"
            className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-3 transition-colors"
            title="Download recurring practice schedule as .ics — imports into Apple/Google/Outlook calendar and auto-updates weekly."
          >
            <CalendarPlus className="w-3 h-3" /> Add to Calendar
          </button>
          <a
            href="/events"
            data-testid="next-practice-cta"
            className="hidden lg:inline-flex items-center gap-2 bg-[#0a1f44] hover:bg-[#0d2555] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-3 transition-colors"
          >
            Schedule <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </section>
  );
}
